import React from 'react';
import { Copy, Check, Apple, Terminal } from 'lucide-react';
import PageHeader from '../ui/PageHeader';
import SectionSurface from '../ui/SectionSurface';
import Button from '../ui/Button';
import { NARROW_WIDTH, PAGE_STACK, TEXT, CODE_BLOCK, TAG_NEUTRAL } from '../../uiTokens';

function InstallBlock({ Icon, title, subtitle, commands, copied, onCopy }) {
  return (
    <SectionSurface className="p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Icon className="h-5 w-5 shrink-0 text-muted" aria-hidden="true" />
          <div className="min-w-0">
            <h2 className={TEXT.h3}>{title}</h2>
            <p className={TEXT.caption}>{subtitle}</p>
          </div>
        </div>
        <Button
          size="sm"
          variant={copied ? 'secondary' : 'outline'}
          onClick={onCopy}
          aria-label={`Copy ${title} install commands`}
        >
          {copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className={`${CODE_BLOCK} p-4 whitespace-pre-wrap break-words overflow-x-auto`}>
        <code>{commands}</code>
      </pre>
    </SectionSurface>
  );
}

function TerminalLine({ comment, command }) {
  return (
    <>
      {comment && <span className="block text-terminal-muted select-none">{`# ${comment}`}</span>}
      <span className="block">
        <span className="text-terminal-muted select-none">$ </span>
        {command}
      </span>
    </>
  );
}

export default function CliPage({ copiedCliBlock, onCopyMac, onCopyLinux }) {
  return (
    <div className={`${NARROW_WIDTH} ${PAGE_STACK}`}>
      <PageHeader
        eyebrow="Install"
        title="Hushify from the terminal"
        description="Create and open one-time links without leaving your shell. Same encryption model as the web app."
        aside={<span className={TAG_NEUTRAL}>macOS · Linux</span>}
      />

      <div className="mt-10 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
        <InstallBlock
          Icon={Apple}
          title="macOS"
          subtitle="Homebrew"
          commands={`brew trust nvteh/brew-tools\nbrew tap nvteh/brew-tools\nbrew install hushify`}
          copied={copiedCliBlock === 'mac'}
          onCopy={onCopyMac}
        />
        <InstallBlock
          Icon={Terminal}
          title="Linux"
          subtitle="Install script"
          commands="curl -fsSL https://www.hushify.io/install | sh"
          copied={copiedCliBlock === 'linux'}
          onCopy={onCopyLinux}
        />
      </div>

      <section className="mt-12 sm:mt-16" aria-labelledby="try-it">
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <h2 id="try-it" className={TEXT.h2}>Try it</h2>
          <span className={TEXT.caption}>Pipe anything in, get a link out</span>
        </div>
        <pre className={`${CODE_BLOCK} p-5 sm:p-6 overflow-x-auto flex flex-col gap-3`}>
          <code className="contents">
            <TerminalLine comment="Wrap a secret that expires in one hour" command="printf 'secret' | hushify wrap -t 1h" />
            <TerminalLine comment="Open a link you received" command="hushify unwrap <token>" />
            <TerminalLine comment="See every option" command="hushify wrap -h" />
          </code>
        </pre>
        <p className={`${TEXT.caption} mt-3`}>
          The CLI talks to www.hushify.io. Secrets are encrypted before they leave your machine.
        </p>
      </section>
    </div>
  );
}
